/**
 * 座標轉換工具模組
 * 用途：將 TWD97 (EPSG:3826) 座標轉換為 WGS84 (EPSG:4326)
 */

import proj4 from 'proj4';
import { FeatureCollection, Feature } from 'geojson';

// TWD97 (EPSG:3826) 定義
// 參考來源：https://epsg.io/3826
const EPSG3826 =
  '+proj=tmerc +lat_0=0 +lon_0=121 +k=0.9999 +x_0=250000 +y_0=0 +ellps=GRS80 +units=m +no_defs';

// WGS84 (EPSG:4326) 定義
const EPSG4326 = 'EPSG:4326';

// 初始化轉換器（效能優化：避免重複建立）
const transformer = proj4(EPSG3826, EPSG4326);

/**
 * 轉換單一座標或座標陣列
 * @param coords - 原始座標（TWD97 格式）
 * @param geometryType - 幾何類型
 * @returns 轉換後的座標（WGS84 格式）
 */
const transformCoordinates = (coords: any, geometryType: string): any => {
  switch (geometryType) {
    case 'Point':
      // 點：[x, y] -> [lon, lat]
      return transformer.forward(coords);

    case 'LineString':
    case 'MultiPoint':
      // 線或多點：[[x1, y1], [x2, y2], ...] -> [[lon1, lat1], [lon2, lat2], ...]
      return coords.map((coord: number[]) => transformer.forward(coord));

    case 'Polygon':
    case 'MultiLineString':
      // 多邊形或多線：[[[x, y], ...], ...] -> [[[lon, lat], ...], ...]
      return coords.map((ring: number[][]) =>
        ring.map((coord: number[]) => transformer.forward(coord))
      );

    case 'MultiPolygon':
      // 多多邊形：[[[[x, y], ...], ...], ...] -> [[[[lon, lat], ...], ...], ...]
      return coords.map((polygon: number[][][]) =>
        polygon.map((ring: number[][]) =>
          ring.map((coord: number[]) => transformer.forward(coord))
        )
      );

    default:
      console.warn(`未知的幾何類型: ${geometryType}`);
      return coords;
  }
};

/**
 * 轉換 GeoJSON FeatureCollection 從 TWD97 到 WGS84
 * @param geojson - 原始 GeoJSON（TWD97 座標系統）
 * @returns 轉換後的 GeoJSON（WGS84 座標系統）
 * @throws 若轉換過程發生錯誤
 */
export const convertTwd97ToWgs84 = (geojson: FeatureCollection): FeatureCollection => {
  try {
    return {
      ...geojson,
      features: geojson.features.map((feature: Feature) => {
        if (!feature.geometry) {
          console.warn('發現沒有 geometry 的 feature，跳過轉換');
          return feature;
        }

        // 排除 GeometryCollection 類型
        if (feature.geometry.type === 'GeometryCollection') {
          console.warn('不支援 GeometryCollection 類型，跳過轉換');
          return feature;
        }

        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: transformCoordinates(
              (feature.geometry as any).coordinates,
              feature.geometry.type
            ),
          },
        };
      }),
    };
  } catch (error) {
    console.error('座標轉換失敗:', error);
    throw new Error(
      `座標轉換錯誤: ${error instanceof Error ? error.message : String(error)}`
    );
  }
};

export type SourceCrs = 'TWD97' | 'WGS84';

/**
 * 抓取 GeoJSON 並（視 sourceCrs）轉為 WGS84。失敗時回傳空 FeatureCollection。
 * @param sourceCrs 原始座標系統，預設 'TWD97'；若已是 'WGS84' 則跳過轉換
 */
export const fetchAndConvertGeoJSON = async (
  url: string,
  logLabel?: string,
  sourceCrs: SourceCrs = 'TWD97',
): Promise<FeatureCollection> => {
  const label = logLabel ?? url.split('/').pop() ?? url;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = (await response.json()) as FeatureCollection;
    const geojsonWgs84 = sourceCrs === 'WGS84' ? raw : convertTwd97ToWgs84(raw);

    if (process.env.NODE_ENV === 'development') {
      const isValid = validateWgs84Coordinates(geojsonWgs84);
      console.log(`[${label}] 座標驗證 (source=${sourceCrs}):`, isValid ? '通過' : '失敗');
    }

    return geojsonWgs84;
  } catch (error) {
    console.error(`[${label}] 資料抓取或轉換失敗:`, error);
    return { type: 'FeatureCollection', features: [] };
  }
};

/**
 * 驗證座標轉換結果是否合理
 * @param geojson - 轉換後的 GeoJSON
 * @returns 是否通過驗證
 */
export const validateWgs84Coordinates = (geojson: FeatureCollection): boolean => {
  try {
    for (const feature of geojson.features) {
      if (!feature.geometry) continue;

      // 排除 GeometryCollection 類型
      if (feature.geometry.type === 'GeometryCollection') continue;

      const coords = (feature.geometry as any).coordinates;
      const checkCoord = (coord: number[]): boolean => {
        const [lon, lat] = coord;
        // 台灣地區合理範圍：經度 119-122°E，緯度 21-26°N
        return lon >= 119 && lon <= 122 && lat >= 21 && lat <= 26;
      };

      // 簡化版檢查（僅檢查第一個座標點）
      if (feature.geometry.type === 'Point') {
        if (!checkCoord(coords as number[])) return false;
      } else if (feature.geometry.type === 'LineString') {
        if ((coords as number[][]).length > 0 && !checkCoord(coords[0])) return false;
      }
    }
    return true;
  } catch {
    return false;
  }
};
